import RichTextEditor from '@/components/rich-text-editor'
import type { StoreCollectionItem, StoreCollectionMeta, StoreThemeProduct } from '@/lib/api/commerce/store-themes'
import {
    STORE_COLLECTION_KINDS,
    STORE_COLLECTION_MAX_PRODUCTS,
    STORE_COLLECTION_SORTS,
    storeCollectionHasSort,
    type StoreCollectionKind,
} from '@mediardex/contracts'
import { useEffect, useMemo, useState } from 'react'
import { MdAdd, MdArrowDownward, MdArrowUpward, MdClose, MdDelete, MdImage, MdSave, MdSearch } from 'react-icons/md'
import ThemeFieldInput, { type ThemeFieldOptions } from './theme-field'

const SEO_TITLE_MAX = 70
const SEO_DESCRIPTION_MAX = 300

/** Categorias marcáveis (regra "Por categoria"). */
export function CategoryChecklist({
    categories,
    value,
    onChange,
}: {
    categories: { id: number; name: string }[]
    value: number[]
    onChange: (ids: number[]) => void
}) {
    if (categories.length === 0) {
        return <p className="text-sm text-base-content/60">A empresa ainda não tem categorias de produto.</p>
    }
    return (
        <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto rounded-lg border border-base-300 p-2 sm:grid-cols-2">
            {categories.map((category) => (
                <label key={category.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-base-200">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={value.includes(category.id)}
                        onChange={(e) =>
                            onChange(e.target.checked ? [...value, category.id] : value.filter((id) => id !== category.id))
                        }
                    />
                    <span className="truncate text-sm">{category.name}</span>
                </label>
            ))}
        </div>
    )
}

function ProductThumb({ product }: { product: StoreThemeProduct | undefined }) {
    return product?.photo ? (
        <img src={product.photo} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
    ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-base-200 text-base-content/40">
            <MdImage />
        </span>
    )
}

/** Produtos da coleção manual: a lista escolhida (com ordem) e a busca nos produtos da loja. */
function ProductPicker({
    products,
    loading,
    value,
    onChange,
}: {
    products: StoreThemeProduct[]
    loading: boolean
    value: number[]
    onChange: (ids: number[]) => void
}) {
    const [search, setSearch] = useState('')
    const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])
    const term = search.trim().toLowerCase()
    const available = products.filter((p) => !value.includes(p.id) && (!term || p.name.toLowerCase().includes(term)))

    const move = (index: number, delta: number) => {
        const next = [...value]
        const [moved] = next.splice(index, 1)
        next.splice(index + delta, 0, moved)
        onChange(next)
    }

    return (
        // Uma lista embaixo da outra: lado a lado, no painel do editor, o nome do produto
        // não cabe.
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    Na coleção ({value.length})
                </span>
                <ul className="flex max-h-80 min-h-24 flex-col gap-1 overflow-y-auto rounded-lg border border-base-300 p-2">
                    {value.length === 0 && (
                        <li className="p-3 text-center text-sm text-base-content/60">Adicione produtos da lista ao lado.</li>
                    )}
                    {value.map((id, index) => {
                        const product = byId.get(id)
                        return (
                            <li key={id} className="flex items-center gap-2 rounded px-1 py-1 hover:bg-base-200">
                                <span className="w-6 text-right text-xs text-base-content/50">{index + 1}</span>
                                <ProductThumb product={product} />
                                <span className={`flex-1 truncate text-sm ${product?.active === false ? 'text-base-content/50' : ''}`}>
                                    {product?.name ?? `Produto #${id}`}
                                    {product?.active === false && ' (inativo)'}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs btn-square"
                                    disabled={index === 0}
                                    onClick={() => move(index, -1)}
                                    title="Subir"
                                >
                                    <MdArrowUpward />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs btn-square"
                                    disabled={index === value.length - 1}
                                    onClick={() => move(index, 1)}
                                    title="Descer"
                                >
                                    <MdArrowDownward />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs btn-square text-error"
                                    onClick={() => onChange(value.filter((v) => v !== id))}
                                    title="Tirar da coleção"
                                >
                                    <MdClose />
                                </button>
                            </li>
                        )
                    })}
                </ul>
            </div>

            <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Produtos da loja</span>
                <label className="input input-bordered input-sm flex items-center gap-2">
                    <MdSearch className="text-base-content/50" />
                    <input
                        type="text"
                        className="grow"
                        placeholder="Buscar pelo nome"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>
                <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto rounded-lg border border-base-300 p-2">
                    {loading && (
                        <li className="flex justify-center p-3">
                            <span className="loading loading-spinner loading-sm" />
                        </li>
                    )}
                    {!loading && available.length === 0 && (
                        <li className="p-3 text-center text-sm text-base-content/60">
                            {products.length === 0 ? 'A loja ainda não tem produtos.' : 'Nenhum produto a adicionar.'}
                        </li>
                    )}
                    {available.slice(0, 100).map((product) => (
                        <li key={product.id}>
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-base-200 disabled:opacity-50"
                                disabled={value.length >= STORE_COLLECTION_MAX_PRODUCTS}
                                onClick={() => onChange([...value, product.id])}
                            >
                                <ProductThumb product={product} />
                                <span className={`flex-1 truncate text-sm ${product.active ? '' : 'text-base-content/50'}`}>
                                    {product.name}
                                    {!product.active && ' (inativo)'}
                                </span>
                                <MdAdd className="text-primary" />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

interface CollectionSettingsProps {
    collection: StoreCollectionItem
    categories: { id: number; name: string }[]
    products: StoreThemeProduct[]
    productsLoading: boolean
    options: ThemeFieldOptions
    saving: boolean
    /** `productIds` só vem quando a lista da coleção manual mudou. */
    onSave: (meta: StoreCollectionMeta, productIds?: number[]) => void
    onDelete: () => void
}

function formFrom(collection: StoreCollectionItem) {
    return {
        title: collection.title,
        slug: collection.slug,
        visible: collection.visible,
        kind: collection.kind,
        categoryIds: collection.categoryIds,
        sort: collection.sort,
        description: collection.description ?? '',
        image: collection.image ?? '',
        badge: collection.badge ?? '',
        seoTitle: collection.seoTitle ?? '',
        seoDescription: collection.seoDescription ?? '',
        seoImage: collection.seoImage ?? '',
        productIds: collection.products.map((p) => p.catalogId),
    }
}

const sameIds = (a: number[], b: number[]) => a.length === b.length && a.every((id, i) => id === b[i])

/**
 * Coleção da loja: título, endereço, regra dos produtos, capa, texto e SEO. Vale ao
 * salvar (não espera a publicação do tema).
 */
export default function CollectionSettings({
    collection,
    categories,
    products,
    productsLoading,
    options,
    saving,
    onSave,
    onDelete,
}: CollectionSettingsProps) {
    const [form, setForm] = useState(() => formFrom(collection))

    useEffect(() => {
        setForm(formFrom(collection))
    }, [collection])

    const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
        setForm((current) => ({ ...current, [key]: value }))

    const initial = useMemo(() => formFrom(collection), [collection])
    const productsChanged = form.kind === 'MANUAL' && !sameIds(form.productIds, initial.productIds)
    const metaChanged = (Object.keys(initial) as (keyof typeof initial)[]).some((key) =>
        key === 'productIds'
            ? false
            : key === 'categoryIds'
              ? !sameIds([...form.categoryIds].sort(), [...initial.categoryIds].sort())
              : form[key] !== initial[key],
    )
    const invalid = !form.title.trim() || !form.slug || (form.kind === 'CATEGORY' && form.categoryIds.length === 0)

    const handleSave = () =>
        onSave(
            {
                title: form.title,
                slug: form.slug.replace(/^-+|-+$/g, ''),
                visible: form.visible,
                kind: form.kind,
                categoryIds: form.categoryIds,
                sort: form.sort,
                description: form.description || null,
                image: form.image || null,
                badge: form.kind === 'MANUAL' ? form.badge.trim() || null : null,
                seoTitle: form.seoTitle || null,
                seoDescription: form.seoDescription || null,
                seoImage: form.seoImage || null,
            },
            productsChanged ? form.productIds : undefined,
        )

    const kindHelp = STORE_COLLECTION_KINDS.find((k) => k.value === form.kind)?.help

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Coleção</h3>
                <span className="text-xs text-base-content/60">As alterações valem ao salvar, sem publicar o tema.</span>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Título</span>
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={120}
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Endereço</span>
                    <label className="input input-bordered flex w-full items-center gap-1">
                        <span className="text-base-content/50">/collection/</span>
                        <input
                            type="text"
                            className="grow"
                            maxLength={60}
                            value={form.slug}
                            onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                        />
                    </label>
                    <span className="text-xs text-base-content/60">Letras minúsculas, números e hífen. Ex.: colecao-de-verao</span>
                </div>
                <label className="flex cursor-pointer items-center justify-between gap-4 md:col-span-2">
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">Página da coleção na loja</span>
                        <span className="text-xs text-base-content/60">
                            Desligada, o endereço não abre e a vitrine de produtos que usa a coleção fica sem o "Ver todos".
                        </span>
                    </span>
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={form.visible}
                        onChange={(e) => set('visible', e.target.checked)}
                    />
                </label>
            </div>

            <div className="divider my-0 text-xs text-base-content/50">Produtos</div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Como os produtos entram</span>
                    <select
                        className="select select-bordered w-full"
                        value={form.kind}
                        onChange={(e) => set('kind', e.target.value as StoreCollectionKind)}
                    >
                        {STORE_COLLECTION_KINDS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {kindHelp && <span className="text-xs text-base-content/60">{kindHelp}</span>}
                </div>
                {storeCollectionHasSort(form.kind) && (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">Ordem</span>
                        <select
                            className="select select-bordered w-full"
                            value={form.sort}
                            onChange={(e) => set('sort', e.target.value)}
                        >
                            {STORE_COLLECTION_SORTS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {form.kind === 'CATEGORY' && (
                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">Categorias</span>
                    <CategoryChecklist categories={categories} value={form.categoryIds} onChange={(ids) => set('categoryIds', ids)} />
                </div>
            )}

            {form.kind === 'MANUAL' && (
                <>
                    <div className="flex flex-col gap-1.5 md:max-w-sm">
                        <span className="text-sm font-medium">Selo de campanha</span>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex.: Ofertas da semana"
                            maxLength={30}
                            value={form.badge}
                            onChange={(e) => set('badge', e.target.value)}
                        />
                        <span className="text-xs text-base-content/60">
                            Aparece na foto destes produtos em toda listagem da loja. Vazio: sem selo.
                        </span>
                    </div>
                    <ProductPicker
                        products={products}
                        loading={productsLoading}
                        value={form.productIds}
                        onChange={(ids) => set('productIds', ids)}
                    />
                </>
            )}

            <div className="divider my-0 text-xs text-base-content/50">Topo da página</div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <ThemeFieldInput
                    name="image"
                    field={{ type: 'image', label: 'Imagem de capa', help: 'Opcional. Largura recomendada: 1600 px.' }}
                    value={form.image}
                    onChange={(value) => set('image', (value as string) ?? '')}
                    options={options}
                />
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Texto acima dos produtos</span>
                    <RichTextEditor
                        content={form.description}
                        onChange={(html) => set('description', html === '<p></p>' ? '' : html)}
                        minHeight="160px"
                    />
                </div>
            </div>

            <div className="divider my-0 text-xs text-base-content/50">Buscadores e compartilhamento</div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Título nos buscadores</span>
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={SEO_TITLE_MAX}
                        placeholder={form.title}
                        value={form.seoTitle}
                        onChange={(e) => set('seoTitle', e.target.value)}
                    />
                    <span className="text-xs text-base-content/60">
                        {form.seoTitle.length}/{SEO_TITLE_MAX}. Vazio: usa o título da coleção.
                    </span>
                </div>
                <div className="flex flex-col gap-1.5 md:row-span-2">
                    <ThemeFieldInput
                        name="seoImage"
                        field={{
                            type: 'image',
                            label: 'Imagem de compartilhamento',
                            help: 'Aparece no WhatsApp e nas redes. Vazio: usa a capa. Recomendado: 1200 x 630 px.',
                        }}
                        value={form.seoImage}
                        onChange={(value) => set('seoImage', (value as string) ?? '')}
                        options={options}
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Descrição nos buscadores</span>
                    <textarea
                        className="textarea textarea-bordered w-full"
                        rows={3}
                        maxLength={SEO_DESCRIPTION_MAX}
                        value={form.seoDescription}
                        onChange={(e) => set('seoDescription', e.target.value)}
                    />
                    <span className="text-xs text-base-content/60">
                        {form.seoDescription.length}/{SEO_DESCRIPTION_MAX}. O Google costuma mostrar até 160.
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-base-200 pt-4">
                <button type="button" className="btn btn-sm btn-ghost text-error" onClick={onDelete}>
                    <MdDelete /> Remover coleção
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={(!metaChanged && !productsChanged) || saving || invalid}
                    onClick={handleSave}
                >
                    {saving ? <span className="loading loading-spinner loading-xs" /> : <MdSave />} Salvar
                </button>
            </div>
        </div>
    )
}
